/**
 * google-identity.adapter — Google Identity Services (GIS)
 *
 * 🔑 **ทำไมอยู่ใน `shared/` ไม่ใช่ `member/`** — "ให้ผู้ใช้ยืนยันตัวตนด้วยบัญชี Google"
 *    ไม่ผูกกับธุรกิจฝั่งไหน · `activities-admin--web` (Day 7) ที่แอดมิน login ด้วย Google
 *    ใช้ตัวเดียวกันได้ ต่างกันแค่ `clientId` ที่แอปส่งเข้ามา
 *
 * ⚠️ **type ประกาศที่นี่เอง ไม่ import `GoogleIdentityPort` ของ feature** — เหตุผลเดียวกับ
 *    `clipboard.adapter.ts` (structural typing ประกอบเข้าหากันได้โดยไม่มีใครพึ่งใคร)
 *
 * 🔴 **id_token flow** — เบราว์เซอร์ได้ `id_token` มาเองแล้ว POST เข้า `/auth/google`
 *    ⇒ **ไม่มี client secret · ไม่มี callback URL** · Google Cloud Console ต้องตั้ง
 *    **Authorized JavaScript origins** (checklist D.7-D.8) ไม่ใช่ redirect URI
 *
 * 🕳️ **ข้อจำกัดที่รู้ตัวของการใช้ `prompt()` (One Tap)**
 *    เบราว์เซอร์/ตัวบล็อกโฆษณาบางตัวปิด One Tap ได้ · ทางที่ทนกว่าคือ `renderButton()`
 *    ซึ่งต้องให้ page ยื่น DOM node ให้ GIS วาดปุ่มเอง = page ต้องรู้จัก SDK
 *    ⇒ **แลกกับการทำลายเส้นแบ่ง port ที่ทั้ง repo ยืนอยู่** · ถ้าวันหนึ่งวัดได้ว่าผู้ใช้จริง
 *    ติดปัญหานี้บ่อย ให้เปลี่ยน **ที่ไฟล์นี้ + เพิ่ม slot ใน port** ไม่ใช่ให้ page import GIS ตรง ๆ
 *
 * 🔴 **บั๊กจริงที่เจอบน staging 2026-08-08 (QA) — และเหตุผลของโครงในไฟล์นี้**
 *    โค้ดเดิมมีทางออกจาก promise ทางเดียว: `isNotDisplayed() || isSkippedMoment()`
 *    ซึ่งเป็น **2 method ที่ Google ประกาศเลิกใช้เมื่อ FedCM บังคับ** (`[GSI_LOGGER]` เตือน
 *    ไว้ใน console อยู่แล้ว) ⇒ วันที่มันหยุดทำงาน promise **ไม่มีวัน settle**
 *    ผู้ใช้เห็นปุ่มค้างที่ "กำลังเข้าสู่ระบบ…" ตลอดกาล · ไม่มี error ไม่มี request
 *    ไม่มีทางกลับ ต้องรีเฟรชอย่างเดียว — **แย่กว่าปุ่มที่กดแล้วเงียบ เพราะมันแกล้งทำเป็นทำงาน**
 *
 *    ⇒ โครงใหม่ยืนบน 3 อย่าง:
 *      1. **`getMomentType()` ก่อนเสมอ** (ตัวที่ยังอยู่) · method ที่ถูก deprecate เป็นแค่ fallback
 *      2. **ทุกการเรียก method ของ SDK ห่อ try/catch** — หายไป/throw ต้องไม่ทำให้ทั้ง flow ตาย
 *      3. **timeout เป็นตาข่ายชั้นสุดท้าย** — ไม่ว่า SDK จะทำอะไร ผู้ใช้ต้องได้คำตอบเสมอ
 *
 *    🔑 กติกาที่ได้กว้างกว่าเคสนี้: **โค้ดที่รอ callback ของ SDK ภายนอก
 *       ต้องมีทางออกที่ไม่ต้องพึ่ง SDK นั้น**
 */
const GIS_SRC = 'https://accounts.google.com/gsi/client';

export type GoogleIdentity = {
  /** โหลด + init GIS ด้วย client id — ต้องเรียกก่อนใช้อย่างอื่น */
  init(): Promise<void>;
  /**
   * เปิดหน้าต่างให้ผู้ใช้เลือกบัญชี Google แล้วคืน `id_token`
   * · `null` = **ผู้ใช้ยกเลิก/ปิดหน้าต่าง** ซึ่งเป็นทางเดินปกติ ไม่ใช่ error
   */
  requestIdToken(): Promise<string | null>;
};

type GisCredentialResponse = { credential?: string };

/**
 * รูปของ notification ที่ `prompt()` ส่งกลับมา — **ทุก method เป็น optional โดยตั้งใจ**
 *
 * 🔴 Google ประกาศเลิกใช้ `isNotDisplayed()` · `isSkippedMoment()` · `getSkippedReason()`
 *    เมื่อ FedCM บังคับใช้ (`[GSI_LOGGER]` เตือนไว้ใน console) ⇒ **ห้ามสมมติว่ามันมีหรือทำงาน**
 *    ตัวที่ยังอยู่คือ `getMomentType()` · `isDismissedMoment()` · `getDismissedReason()`
 */
type GisPromptNotification = {
  getMomentType?(): string;
  getSkippedReason?(): string;
  getDismissedReason?(): string;
  isNotDisplayed?(): boolean;
  isSkippedMoment?(): boolean;
  isDismissedMoment?(): boolean;
};

type GisIdApi = {
  initialize(config: {
    client_id: string;
    callback: (res: GisCredentialResponse) => void;
    cancel_on_tap_outside?: boolean;
  }): void;
  prompt(listener?: (notification: GisPromptNotification) => void): void;
};

/** global ที่ GIS ติดตั้งไว้ — ประกาศเท่าที่ใช้จริง ไม่ลาก type ทั้งก้อนเข้ามา */
type WindowWithGis = Window & { google?: { accounts?: { id?: GisIdApi } } };

/**
 * ข้อความเดียวที่ผู้ใช้จะเห็นเมื่อ **One Tap ขึ้นไม่ได้** — ไม่ใช่ "ยกเลิก"
 *
 * 🔴 เขียนให้บอก **ทางออก** ไม่ใช่แค่บอกว่าพัง — กลุ่มผู้ใช้ 60+ เจอ "เกิดข้อผิดพลาด"
 *    เฉย ๆ แล้วไปต่อไม่ถูก · LINE เป็นทางที่ใช้ได้จริงอยู่แล้วสำหรับสมาชิกส่วนใหญ่
 */
const CANNOT_SHOW_MESSAGE =
  'เปิดหน้าต่างเลือกบัญชี Google ไม่ได้ — ลองปิดตัวบล็อกโฆษณา หรืออนุญาตคุกกี้ของ google.com แล้วลองใหม่\n' +
  'ถ้ายังไม่ได้ สมาชิกที่ใช้ LINE อยู่แล้วให้เปิดจากเมนูในแชท LINE ของ THE ELITE';

/**
 * ⏱️ **ตาข่ายชั้นสุดท้าย — ห้าม promise ค้างตลอดกาลไม่ว่ากรณีใด**
 *
 * 🔴 นี่คือบทเรียนตรงจากบั๊กจริง (QA staging 2026-08-08): โค้ดเดิมพึ่ง
 *    `isNotDisplayed()`/`isSkippedMoment()` เป็น **ทางออกทางเดียว** ของ promise
 *    ⇒ วันที่ Google ทำให้ 2 method นั้นหยุดทำงาน (FedCM) ปุ่มค้างที่ "กำลังเข้าสู่ระบบ…"
 *      **ตลอดกาล** · ไม่มี error ไม่มี request ไม่มีทางกลับ ต้องรีเฟรชอย่างเดียว
 *    ⇒ กติกาที่ได้: **โค้ดที่รอ callback ของ SDK ภายนอก ต้องมีทางออกที่ไม่ต้องพึ่ง SDK นั้น**
 */
const NO_MOMENT_TIMEOUT_MS = 15_000;
/** หลังกล่องขึ้นแล้ว ให้เวลาผู้ใช้เลือกบัญชีนาน ๆ — แต่ **ยังต้องมีเพดาน** */
const AFTER_DISPLAY_TIMEOUT_MS = 180_000;

/** `momentType` ที่แปลว่า **ผู้ใช้ตั้งใจเอง** ⇒ ยกเลิกเงียบ ๆ ไม่ใช่ error */
const USER_INTENT_SKIP_REASONS = new Set(['user_cancel', 'tap_outside', 'auto_cancel']);

/** เรียก method ของ SDK ที่ **อาจไม่มีหรืออาจ throw** — คืน `undefined` แทนที่จะระเบิด */
function safeCall(fn: (() => string | boolean) | undefined): string | boolean | undefined {
  if (typeof fn !== 'function') return undefined;
  try {
    return fn();
  } catch {
    return undefined;
  }
}

/**
 * อ่านว่า moment นี้คืออะไร — **ใช้ `getMomentType()` ก่อนเสมอ**
 * แล้วค่อยตกไปใช้ method ที่ Google ประกาศเลิกใช้ (ซึ่งอาจหายไปเงียบ ๆ)
 */
function readMomentType(n: GisPromptNotification): 'display' | 'skipped' | 'dismissed' | 'unknown' {
  const t = safeCall(n.getMomentType?.bind(n));
  if (t === 'display' || t === 'skipped' || t === 'dismissed') return t;

  if (safeCall(n.isDismissedMoment?.bind(n)) === true) return 'dismissed';
  if (safeCall(n.isSkippedMoment?.bind(n)) === true) return 'skipped';
  if (safeCall(n.isNotDisplayed?.bind(n)) === true) return 'skipped';
  return 'unknown';
}

function loadScriptOnce(): Promise<void> {
  const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
  if (existing !== null) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = GIS_SRC;
    el.async = true;
    el.onload = () => resolve();
    /**
     * โหลดไม่ได้ = เน็ตพัง หรือ **ตัวบล็อกโฆษณาบล็อกโดเมนของ Google**
     * ⇒ ต้องเป็น error ที่ผู้ใช้เห็นข้อความ ไม่ใช่ปุ่มที่กดแล้วเงียบตลอดกาล
     */
    el.onerror = () => reject(new Error('โหลดสคริปต์ของ Google ไม่ได้ (เน็ตหรือตัวบล็อกโฆษณา)'));
    document.head.append(el);
  });
}

export function createGoogleIdentityAdapter(clientId: string): GoogleIdentity {
  let initialized = false;

  return {
    async init() {
      if (initialized) return;
      await loadScriptOnce();
      if ((window as WindowWithGis).google?.accounts?.id === undefined) {
        throw new Error('สคริปต์ Google โหลดแล้วแต่ไม่พบ API — เวอร์ชันของ GIS เปลี่ยนไป?');
      }
      initialized = true;
    },

    async requestIdToken() {
      const api = (window as WindowWithGis).google?.accounts?.id;
      if (api === undefined) throw new Error('เรียก init() ก่อนใช้ requestIdToken()');

      return new Promise<string | null>((resolve, reject) => {
        let settled = false;
        let timer: ReturnType<typeof setTimeout>;

        const cancelled = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(null);
        };
        const authenticated = (credential: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(credential);
        };
        const cannotShow = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(new Error(CANNOT_SHOW_MESSAGE));
        };
        const armTimer = (ms: number) => {
          clearTimeout(timer);
          timer = setTimeout(cannotShow, ms);
        };

        /** ตั้งไว้ก่อนเรียก SDK — ถ้า SDK เงียบสนิท ตัวนี้คือคนตอบผู้ใช้ */
        armTimer(NO_MOMENT_TIMEOUT_MS);

        /**
         * ⚠️ `initialize` ต้องเรียกใหม่ทุกครั้งที่ขอ token — callback ผูกกับ closure
         *    ของรอบนี้ · เรียกครั้งเดียวตอน init แล้ว resolve promise เก่าไม่ได้
         */
        api.initialize({
          client_id: clientId,
          callback: (res) => {
            // ไม่มี credential ทั้งที่ callback มา = ผู้ใช้ไม่ได้เลือกบัญชี ⇒ ยกเลิก
            if (res.credential === undefined || res.credential.length === 0) cancelled();
            else authenticated(res.credential);
          },
          // กดนอกกล่อง = ยกเลิก (ผู้ใช้เปลี่ยนใจ ไม่ใช่ error)
          cancel_on_tap_outside: true,
        });

        api.prompt((notification) => {
          switch (readMomentType(notification)) {
            case 'display':
              /**
               * กล่องขึ้นแล้ว — **ห้ามตัดสินอะไรตอนนี้** ผู้ใช้กำลังเลือกบัญชีอยู่
               * แค่ยืดเพดานเวลาออกไป (ยังต้องมีเพดาน ห้ามปล่อยไม่มีที่สิ้นสุด)
               */
              armTimer(AFTER_DISPLAY_TIMEOUT_MS);
              return;

            case 'dismissed': {
              /**
               * 🔴 `credential_returned` = กล่องปิดเพราะ **login สำเร็จ** —
               *    ห้าม resolve(null) ตรงนี้เด็ดขาด ไม่งั้นจะแย่งชิงกับ callback
               *    ที่กำลังจะส่ง credential มา แล้วผู้ใช้จะ "ยกเลิก" ทั้งที่เพิ่งล็อกอินสำเร็จ
               */
              if (safeCall(notification.getDismissedReason?.bind(notification)) === 'credential_returned') return;
              cancelled();
              return;
            }

            case 'skipped': {
              /**
               * 🔑 **จุดที่ต่างจากโค้ดเดิม** — เดิมถือว่า skipped = ยกเลิกทั้งหมด
               *    แต่ `unknown_reason`/`issuing_failed` แปลว่า **กล่องขึ้นไม่ได้**
               *    ซึ่งผู้ใช้ไม่ได้ทำอะไรผิดและไม่มีทางรู้ว่าต้องทำยังไงต่อ
               *    ⇒ ต้องได้ข้อความบอกทางออก ไม่ใช่ปุ่มที่เด้งกลับเป็นปกติเฉย ๆ
               * ⚠️ `getSkippedReason()` เป็นตัวที่ Google กำลังเลิกใช้ ⇒ อ่านไม่ได้ = ถือว่าขึ้นไม่ได้
               */
              const reason = safeCall(notification.getSkippedReason?.bind(notification));
              if (typeof reason === 'string' && USER_INTENT_SKIP_REASONS.has(reason)) cancelled();
              else cannotShow();
              return;
            }

            default:
              /**
               * อ่าน moment ไม่ออกเลย — **ไม่เดา** ปล่อยให้ timer เป็นคนตัดสิน
               * (เดาว่า "ยกเลิก" = ปุ่มเด้งกลับเงียบ ๆ แล้วผู้ใช้กดวนไม่รู้จบ)
               */
              return;
          }
        });
      });
    },
  };
}
